/**
 * MessagesContainer.tsx
 * Talkroute
 *
 * Created by Vel Genov on 3/25/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { animateScroll } from "react-scroll";
import { Loader } from "semantic-ui-react";

import { WithStyles, withStyles } from "@material-ui/core";

import {
  getAllMessagesSinceLast,
  findConversationById,
  getNextBacthMessages,
  fetchMessagesStarted,
} from "../../actions/conversationActions";
import {
  ConversationData,
  NEW_CONVERSATION_ID,
  WELCOME_CONVERSATION,
  parseConversationToData,
} from "../../services/dataClasses/ConversationData";
import SingleMessageView from "./SingleMessageView";
import { objectMapToArray } from "../../helpers/objectFunctions";

import ChevronDownIcon from "@resources/icons/arrow-down.svg";

// The maximum duration between 2 timestamps in hours
const durationBetweenTimestamp = 1;
// The height of message container
let containerHeight = 0;
// It indicates whether the app loads the old messages or not
let isLoadingOld = false;

interface IMessagesContainerProps extends WithStyles<typeof styles> {
  conversations: any[];
  currentConversationId: string;
  isFetchingMessages: boolean;
  temporaryConversation: null | ConversationData;
  getAllMessagesSinceLast: (payload: {
    conversations: any[];
    conversationId: string;
  }) => any;
  getNextBacthMessages: (payload: {
    conversations: any[];
    conversationId: string;
  }) => any;
  fetchMessagesStarted: () => any;
}

const styles = theme => ({
  root: {
    overflowY: "auto" as any,
    overflowX: "hidden" as any,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "auto",
    position: "relative" as any,
    background: "white"
  },
  scrollToBottomButton: {
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    position: "fixed" as any,
    bottom: "90px",
    right: "15px",
    zIndex: 5,
    borderRadius: "50%",
    backgroundColor: "var(--color-brown-grey)",
    border: 0,
    width: "40px",
    height: "40px",
    cursor: "pointer",
    outline: 0
  },
});


/**
 * Display messages from a conversation
 *
 */
class MessagesContainer extends React.Component<IMessagesContainerProps, any> {
  public messagesView;
  public messagesEnd;

  static defaultProps = {
    conversations: [],
    isFetchingMessages: false,
  };

  /**
   * Draw the messages
   */
  showMessages = () => {
    const { currentConversationId, conversations, temporaryConversation } = this.props;

    const currentConversationPosition = findConversationById({
      conversationId: currentConversationId,
      conversations,
    });
 
    if (currentConversationPosition !== undefined || temporaryConversation) {
      const currentConversation = temporaryConversation ? temporaryConversation : conversations[currentConversationPosition];
      if (currentConversation) {
        const { messages } = currentConversation;
        if (messages) {
          let lastTimeStampDate = moment.unix(0);
          const returnValue = objectMapToArray(messages, (message) => {
            const { id, createdAt } = message;

            // Show a date if messages are more than 1h apart
            let showDateStamp = false;
            const duration = moment.duration(lastTimeStampDate.diff(createdAt));
            if (Math.abs(duration.asHours()) >= durationBetweenTimestamp) {
              showDateStamp = true;
              lastTimeStampDate = createdAt;
            }

            return (
              <SingleMessageView
                key={id}
                shouldShowDateStamp={showDateStamp}
                message={message}
                conversationId={currentConversationId}
              />
            );
          });

          return returnValue;
        }
      }
    }

    return null;
  };

  /**
   * Callback when the conversation display has scrolled
   * Checks if the conversation view scrolled to the top and loads more conversations if so
   *
   * The getNextBatchMessages action is responsible for checking if there are more messages avaialble
   */
  messagesScrolled = (e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const SCROLL_VIEW_TOP = 0;

    if (scrollTop <= SCROLL_VIEW_TOP && scrollHeight > clientHeight) {
      const {
        getNextBacthMessages: getNextBacthMessagesLocal,
        fetchMessagesStarted: fetchMessagesStartedLocal,
        conversations,
        currentConversationId: conversationId,
      } = this.props;
      const currentHeight = this.messagesView.scrollHeight;

      isLoadingOld = true;
      containerHeight = currentHeight;

      fetchMessagesStartedLocal();

      getNextBacthMessagesLocal({ conversations, conversationId });
    }
  };

  isAtBottom = (e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (scrollHeight <= Math.ceil(clientHeight + scrollTop)) {
      this.setState({ showScrollButton: false });
    } else {
      this.setState({ showScrollButton: true });
    }
  };

  /**
   * Request to get all new messages since the last one
   */
  getNewMessages = () => {
    const {
      getAllMessagesSinceLast: getAllMessagesSinceLastLocal,
      currentConversationId,
      conversations,
      temporaryConversation
    } = this.props;

    isLoadingOld = false;

    if (
      currentConversationId !== NEW_CONVERSATION_ID &&
      currentConversationId !== WELCOME_CONVERSATION &&
      temporaryConversation?.conversationId !== currentConversationId
      ) {
        this.setState({ isFirstLoading: true });

      getAllMessagesSinceLastLocal({
        conversationId: currentConversationId,
        conversations,
      });
    }
  };

  /**
   * Scroll the messagesContainer to the bottom of the messages
   */
  scrollToBottom = () => {
    this.setState({ isFirstLoading: false });

    animateScroll.scrollToBottom({
      containerId: "messagesScroll",
      duration: 0,
    });
  };

  /**
   * Scroll the messagesContainer animated to the bottom of the messages
   */
  scrollToBottomAnimated = () => {
    animateScroll.scrollToBottom({
      containerId: "messagesScroll",
      duration: 1500,
      smooth: 'easeInOutQuint',
    });
  };

  /**
   * Set the scroll at the original position.
   */
  stayScrollPos = () => {
    const currentHeight = this.messagesView.scrollHeight;
    const scrollTop = currentHeight - containerHeight;
    this.messagesView.scrollTop = scrollTop;
    containerHeight = currentHeight;
  };

  /**
   * Get the ID of the last message in the conversation
   */
  getLastMessageId() {
    const { currentConversationId, conversations } = this.props;
    const currentConversationPosition = findConversationById({
      conversationId: currentConversationId,
      conversations,
    });
    if (currentConversationPosition !== undefined) {
      const currentConversation = conversations[currentConversationPosition];
      if (currentConversation) {
        const { lastMessage } = currentConversation;
        if (lastMessage) {
          const { id } = lastMessage;

          return id;
        }
      }
    }

    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      // It indicates whether the conversation loading is first or not
      isFirstLoading: false,
      showScrollButton: false,
    };
  }

  componentDidMount() {
    this.scrollToBottom();

    this.getNewMessages();
  }

  componentDidUpdate(prevProps) {
    const {
      currentConversationId: prevConversationId,
      isFetchingMessages: prevIsFetchingMessages,
    } = prevProps;
    const {
      currentConversationId,
      isFetchingMessages,
      conversations,
    } = this.props;
    const currentHeight = this.messagesView.scrollHeight;

    if (currentConversationId !== prevConversationId) {
      const currentConversationPosition = findConversationById({
        conversationId: currentConversationId,
        conversations,
      });

      if (currentConversationPosition !== undefined) {
        const currentConversation = conversations[currentConversationPosition];
        if (currentConversation) {
          const { lastMessageFetch } = currentConversation;
          if (!lastMessageFetch) {
            // The messages are not loaded yet, so loads them
            this.getNewMessages();
          } else {
            // The messages are loaded already, so only scroll to bottom
            this.scrollToBottom();
            containerHeight = currentHeight;
            isLoadingOld = false;
          }
        }
      }
    }
    // verify that on first load the first opened conversation 
    // will be scrolled to the bottom as soon as its loaded
    if (this.state.isFirstLoading) {
      if (currentConversationId === prevConversationId) {
        const currentConversationPosition = findConversationById({
          conversationId: currentConversationId,
          conversations,
        });
        if (currentConversationPosition === 0) {
          const currentConversation = conversations[currentConversationPosition];
          if (currentConversation) {
            const { lastMessageFetch } = currentConversation;
            if (lastMessageFetch) {
              this.scrollToBottom();
              containerHeight = currentHeight;
            }
          }
        }
      }
    }

    if (prevIsFetchingMessages !== isFetchingMessages && !isFetchingMessages) {
      if (isLoadingOld) {
        this.stayScrollPos();
        isLoadingOld = false;
      } else {
        this.scrollToBottom();
        containerHeight = currentHeight;
      }
    }

    if (prevIsFetchingMessages === isFetchingMessages && !isFetchingMessages) {
      isLoadingOld = false;
    }
  }

  render() {
    const { isFirstLoading, showScrollButton } = this.state;
    const { classes } = this.props;

    return (
      <div
        id="messagesScroll"
        className={classes.root}
        onScroll={(e) => {
          this.isAtBottom(e);
          this.messagesScrolled(e);
        }}
        ref={(messagesView) => {
          this.messagesView = messagesView;
        }}
      >
        {showScrollButton && 
          <button
            className={classes.scrollToBottomButton}
            onClick={this.scrollToBottomAnimated}
          >
            <ChevronDownIcon style={{fill: "var(--color-white)"}}/>
          </button>
        }
        <Loader active={isFirstLoading} />

        <div className="chat">
          {this.showMessages()}
          <div
            style={{ float: "left", clear: "both" }}
            ref={(el) => {
              this.messagesEnd = el;
            }}
          />
        </div>
      </div>
    );
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state) => ({
  conversations: state.conversations.data,
  isFetchingMessages: state.conversations.isFetchingMessages,
  temporaryConversation: state.conversations.temporaryConversation,
});

export default connect(mapStateToProps, {
  getAllMessagesSinceLast,
  getNextBacthMessages,
  fetchMessagesStarted,
})(withStyles(styles)(MessagesContainer));
